import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { ListGroupProps } from "./types";
import { useList } from "./ListContext";

export function ListGroup({
  title,
  icon,
  count,
  defaultOpen = true,
  children,
  itemIds,
}: ListGroupProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { state, actions } = useList();

  const checkboxRef = useRef<HTMLInputElement>(null);

  const checkedCount = itemIds
    ? itemIds.filter((id) => state.selectedIds.has(id)).length
    : 0;
  const allChecked = itemIds
    ? checkedCount === itemIds.length && itemIds.length > 0
    : false;
  const indeterminate = itemIds ? checkedCount > 0 && !allChecked : false;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  function handleCheckboxClick(e: MouseEvent) {
    e.stopPropagation();
    if (!itemIds) return;
    if (allChecked) {
      actions.removeFromSelection(itemIds);
    } else {
      actions.addToSelection(itemIds);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-primary rounded-lg hover:bg-card-hover transition-colors"
      >
        {itemIds && (
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={allChecked}
            onChange={() => {
              /* handled by onClick */
            }}
            onClick={handleCheckboxClick}
            className="hidden sm:block h-4 w-4 shrink-0 rounded border border-border checked:bg-accent checked:border-accent"
            style={{ accentColor: "var(--color-accent)" }}
          />
        )}
        <ChevronRight
          className={`h-4 w-4 text-secondary transition-transform duration-200 ${open ? "rotate-90" : "rotate-0"}`}
        />
        {icon && (
          <span className="flex items-center text-secondary">{icon}</span>
        )}
        <span>{title}</span>
        {count != null && <span className="text-secondary">({count})</span>}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2, ease: "easeInOut" },
            }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex flex-col gap-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
